import { Alert, Box, Paper, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_IDLE_LOCK_MINUTES,
  DEFAULT_IDLE_LOGOUT_MINUTES,
  IDLE_LOCK_MINUTES_MAX,
  IDLE_LOCK_MINUTES_MIN,
  IDLE_LOGOUT_MINUTES_MAX,
  IDLE_LOGOUT_MINUTES_MIN,
} from '@/hooks/useTenantSettings';
import { idleTimeoutSaveError } from './idleTimeoutValidation';

interface IdleTimeoutSettingsProps {
  idleLockMinutes: number;
  idleLogoutMinutes: number;
  onChange: (next: { idleLockMinutes: number; idleLogoutMinutes: number }) => void;
  disabled?: boolean;
}

/** Parse the field as a full number. Do not truncate decimals or exponent tails. */
function parseMinutes(raw: string): number {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return Number.NaN;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

/**
 * Workshop idle lock / logout (#2216). Lives on the workshop tab rather than a
 * new tab; it still rides the same PATCH `/tenants/me/settings` save.
 */
export function IdleTimeoutSettings({
  idleLockMinutes,
  idleLogoutMinutes,
  onChange,
  disabled = false,
}: IdleTimeoutSettingsProps) {
  const { t } = useTranslation('settings');
  const errorKey = idleTimeoutSaveError(true, idleLockMinutes, idleLogoutMinutes);

  let errorText: string | undefined;
  if (errorKey === 'lockRange') {
    errorText = t('idleTimeout.lockRange', { min: IDLE_LOCK_MINUTES_MIN, max: IDLE_LOCK_MINUTES_MAX });
  } else if (errorKey === 'logoutRange') {
    errorText = t('idleTimeout.logoutRange', { min: IDLE_LOGOUT_MINUTES_MIN, max: IDLE_LOGOUT_MINUTES_MAX });
  } else if (errorKey === 'logoutMustExceedLock') {
    errorText = t('idleTimeout.logoutMustExceedLock');
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {t('idleTimeout.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('idleTimeout.description')}
      </Typography>
      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 4 }}>
        <Stack spacing={2}>
          {errorText !== undefined && <Alert severity="error">{errorText}</Alert>}
          <TextField
            type="number"
            label={t('idleTimeout.lockLabel')}
            value={Number.isNaN(idleLockMinutes) ? '' : idleLockMinutes}
            onChange={(e) =>
              onChange({
                idleLockMinutes: parseMinutes(e.target.value),
                idleLogoutMinutes,
              })
            }
            disabled={disabled}
            error={errorKey === 'lockRange' || errorKey === 'logoutMustExceedLock'}
            helperText={t('idleTimeout.lockHint', {
              min: IDLE_LOCK_MINUTES_MIN,
              max: IDLE_LOCK_MINUTES_MAX,
              defaultMinutes: DEFAULT_IDLE_LOCK_MINUTES,
            })}
            sx={{ maxWidth: 400 }}
            slotProps={{
              htmlInput: {
                min: IDLE_LOCK_MINUTES_MIN,
                max: IDLE_LOCK_MINUTES_MAX,
                step: 1,
              },
            }}
          />
          <TextField
            type="number"
            label={t('idleTimeout.logoutLabel')}
            value={Number.isNaN(idleLogoutMinutes) ? '' : idleLogoutMinutes}
            onChange={(e) =>
              onChange({
                idleLockMinutes,
                idleLogoutMinutes: parseMinutes(e.target.value),
              })
            }
            disabled={disabled}
            error={errorKey === 'logoutRange' || errorKey === 'logoutMustExceedLock'}
            helperText={t('idleTimeout.logoutHint', {
              min: IDLE_LOGOUT_MINUTES_MIN,
              max: IDLE_LOGOUT_MINUTES_MAX,
              defaultMinutes: DEFAULT_IDLE_LOGOUT_MINUTES,
            })}
            sx={{ maxWidth: 400 }}
            slotProps={{
              htmlInput: {
                min: IDLE_LOGOUT_MINUTES_MIN,
                max: IDLE_LOGOUT_MINUTES_MAX,
                step: 1,
              },
            }}
          />
        </Stack>
      </Paper>
    </Box>
  );
}
